import { photoPlacement, routeFrame, ESRI_CREDIT, type RoutePoint } from './workoutShareDrawing';
import { latinPosterDigits, selectShareMetrics, shareHeight, shareRouteBox, shareBrandBox, type ShareMetric, type ShareMetricId } from './workoutShareModel';
import type { StreamPoint } from './workoutStream';

export type ShareRenderOptions = {
  ribbonImage?: HTMLImageElement | null;
  logoImage?: HTMLImageElement | null;
  lightLogoImage?: HTMLImageElement | null;
  template: number; photo: HTMLImageElement | null; photoX: number; photoY: number; photoZoom: number; photoStrength: number;
  route: RoutePoint[]; routeMode: 'none' | 'line' | 'map'; mapImage: HTMLImageElement | null; stream: StreamPoint[];
  metrics: ShareMetric[]; visible: ShareMetricId[]; hero: ShareMetricId | null; accent: string;
  title: string; date: string; showTitle: boolean; showDate: boolean; rtl: boolean; noRoute: string;
};
export type ShareRenderMode = 'preview' | 'transparent' | 'photo';
export function renderWorkoutShare(canvas: HTMLCanvasElement, o: ShareRenderOptions, thumbnail = false, mode: ShareRenderMode = 'preview') {
  const W=1080, H=shareHeight(o.template), scale=thumbnail ? .13 : 1;
  const output=canvas;
  if(typeof document !== 'undefined') canvas=document.createElement('canvas');
  canvas.width=Math.round(W*scale); canvas.height=Math.round(H*scale);
  const context=canvas.getContext('2d'); if(!context) return;
  const ctx: CanvasRenderingContext2D = context;
  ctx.scale(scale,scale);
  ctx.direction='ltr';ctx.textAlign='left';
  const light=o.template===1 || o.template===3;
  const ink=light?'#141820':'#f7f9fc', muted=light?'#59616d':'#b3beca', accent=light ? (o.accent==='#ffffff'?'#52606e':o.accent==='#bcf725'?'#547800':o.accent) : o.accent;
  const selected=selectShareMetrics(o.metrics,o.visible,o.hero);
  function text(value:string,x:number,y:number,size=28,color=ink,maxWidth=950,weight=600,align:CanvasTextAlign='left',rtl=false) {
    value=latinPosterDigits(value);
    const family=/^[0-9]/.test(value) && !/[\u0600-\u06ff]/.test(value) ? 'Barlow Condensed' : 'Vazir, Inter';
    ctx.save(); ctx.fillStyle=color; ctx.textAlign=align; ctx.direction=rtl?'rtl':'ltr'; ctx.textBaseline='alphabetic';
    ctx.font=`${weight} ${size}px ${family}, sans-serif`;
    const measured=ctx.measureText(value).width;
    if(measured>maxWidth) ctx.font=`${weight} ${size*maxWidth/measured}px ${family}, sans-serif`;
    ctx.fillText(value,x,y); ctx.restore();
  }
  function label(value:string,x:number,y:number,size=24,color=muted,width=950,align:CanvasTextAlign='left') { text(value,x,y,size,color,width,500,align,o.rtl); }
  function metric(m:ShareMetric,x:number,y:number,width:number,size=50,align:CanvasTextAlign='left') {
    text(`${m.value}${m.unit?' '+m.unit:''}`,x,y,size,ink,width,800,align);
    label(m.label,x,y+42,24,muted,width,align);
  }
  function hero(x:number,y:number,width:number,size:number,color=ink) {
    if(!selected.hero) return;
    const m=selected.hero;
    text(m.value,x,y,size,color,width,900);
    label(`${m.label}${m.unit?' · '+m.unit:''}`,x,y+55,30,muted,width);
  }
  function stats(items:ShareMetric[],y:number) {
    const columns=items.length>4?Math.ceil(items.length/2):items.length;
    if(!columns) return;
    if(H===1350 && items.length>4) y=1100;
    const cell=952/columns;
    items.forEach((m,i)=>{const col=i%columns,row=Math.floor(i/columns);const x=64+col*cell;
      if(col){ctx.fillStyle=accent;ctx.fillRect(x-18,y+row*115-38,2,76);}
      metric(m,x,y+row*115,cell-32,columns>3?43:56);
    });
  }
  const includeBackground = mode !== 'transparent';
  ctx.clearRect(0,0,W,H);
  if(includeBackground) {
    ctx.fillStyle=o.template===1?'#f6f1e8':o.template===3?'#ece9f5':o.template===4?'#12202c':'#080d13';ctx.fillRect(0,0,W,H);
    if(o.template===7 && o.ribbonImage) ctx.drawImage(o.ribbonImage,0,0,W,H);
  }
  if(includeBackground && o.photo){
    const p=photoPlacement(o.photo.width,o.photo.height,W,H,o.photoZoom,o.photoX,o.photoY);
    ctx.globalAlpha=o.photoStrength/100;ctx.drawImage(o.photo,p.x,p.y,p.width,p.height);ctx.globalAlpha=1;
    const shade=ctx.createLinearGradient(0,0,0,H);
    shade.addColorStop(0,light?'rgba(250,248,243,.5)':'rgba(3,8,15,.22)');
    shade.addColorStop(.55,light?'rgba(250,248,243,.3)':'rgba(3,8,15,.05)');
    shade.addColorStop(1,light?'rgba(250,248,243,.96)':'rgba(3,8,15,.97)');ctx.fillStyle=shade;ctx.fillRect(0,0,W,H);
  }
  if(o.template===6){
    if(includeBackground){const glow=ctx.createRadialGradient(500,780,10,500,780,850);glow.addColorStop(0,'#14202b');glow.addColorStop(1,'#06090c');ctx.fillStyle=glow;ctx.globalAlpha=o.photo?.85:1;ctx.fillRect(0,0,W,H);ctx.globalAlpha=1;}
    // Decorative contour texture; the highlighted line below is the actual GPS route.
    for(let n=0;n<40;n++){ctx.beginPath();for(let y=-80;y<H+100;y+=18){const x=n*46-350+Math.sin(y/280+n*.09)*155+Math.cos(y/140+n*.16)*48;y===-80?ctx.moveTo(x,y):ctx.lineTo(x,y);}ctx.strokeStyle=n%3?'#ffffff0c':'#ffffff19';ctx.lineWidth=1.3;ctx.stroke();}
    if(selected.hero){ctx.save();ctx.direction='ltr';ctx.textAlign='left';ctx.font='900 350px Barlow Condensed, sans-serif';ctx.strokeStyle='#ffffff14';ctx.lineWidth=2;ctx.strokeText(selected.hero.value,45,1030,990);ctx.restore();}
  }
  if(includeBackground && o.template===4){ctx.fillStyle='#101622';ctx.fillRect(0,1120,W,H-1120);}
  if(o.showDate) text(o.date,1016,100,23,muted,290,500,'right');
  if(o.showTitle) label(o.title,o.rtl?1016:64,190,32,ink,950,o.rtl?'right':'left');
  const [rx,ry,rw,rh]=shareRouteBox(o.template);
  const frame=routeFrame(o.route,rw,rh);
  if(o.template!==7 && o.routeMode!=='none' && frame){
    ctx.save();
    if(includeBackground && o.routeMode==='map' && o.mapImage){
      const layer=document.createElement('canvas');layer.width=rw;layer.height=rh;const lc=layer.getContext('2d');
      if(lc){lc.drawImage(o.mapImage,0,0,rw,rh);lc.globalCompositeOperation='destination-in';const radius=Math.min(rw,rh)/2;
        const fade=lc.createRadialGradient(rw/2,rh/2,radius*.82,rw/2,rh/2,radius);fade.addColorStop(0,'#ffffffe6');fade.addColorStop(1,'#ffffff00');lc.fillStyle=fade;lc.fillRect(0,0,rw,rh);ctx.drawImage(layer,rx,ry);}
    }
    const points=frame.points;ctx.strokeStyle=accent;ctx.lineWidth=o.template>=5?8:6;ctx.lineJoin='round';ctx.lineCap='round';
    if(o.template===0 || o.template===6 || o.template===7){ctx.shadowColor=accent;ctx.shadowBlur=22;}
    ctx.beginPath();points.forEach(([x,y],i)=>i?ctx.lineTo(rx+x,ry+y):ctx.moveTo(rx+x,ry+y));ctx.stroke();ctx.shadowBlur=0;
    [points[0],points[points.length-1]].forEach(([x,y],i)=>{ctx.beginPath();ctx.arc(rx+x,ry+y,14,0,Math.PI*2);ctx.fillStyle=i?ink:accent;ctx.fill();ctx.strokeStyle=i?accent:ink;ctx.lineWidth=4;ctx.stroke();});ctx.restore();
  } else if(o.template!==7 && o.routeMode!=='none') label(o.noRoute,rx+rw/2,ry+rh/2,25,muted,rw,'center');
  const all=selected.hero?[selected.hero,...selected.secondary]:selected.secondary;
  if(o.template===0 || o.template===1){hero(64,selected.secondary.length>4?975:1050,940,180);stats(selected.secondary,1175);}
  else if(o.template===2){
    hero(64,640,900,185,accent);
    if(o.visible.includes('power') || o.visible.includes('np')){
      const valid=o.stream.filter(p=>p.power!=null&&Number.isFinite(p.power));const max=valid.reduce((a,p)=>Math.max(a,p.power!),1),end=o.stream[o.stream.length-1]?.timeSeconds||1;
      ctx.beginPath();let last:number|null=null;o.stream.forEach(p=>{if(p.power==null){last=null;return;}const x=64+p.timeSeconds/end*952,y=1010-p.power/max*220;if(last==null||p.timeSeconds-last>30)ctx.moveTo(x,y);else ctx.lineTo(x,y);last=p.timeSeconds;});ctx.strokeStyle=accent;ctx.lineWidth=3;ctx.stroke();
    }
    stats(selected.secondary,1175);
  } else if(o.template===3){hero(64,340,950,135);stats(selected.secondary,1175);}
  else if(o.template===4){hero(64,460,950,195,'#fff');stats(selected.secondary,1460);}
  else if(o.template===5){hero(64,1460,950,190);stats(selected.secondary,1660);}
  else if(o.template===6){hero(64,1540,950,150);stats(selected.secondary,1700);}
  else if(o.template===7){
    // The light filament is decorative; this template does not distort the GPS track into a ribbon.
    const rows=selected.secondary.length;
    selected.secondary.forEach((m,i)=>{
      const y=340+i*Math.min(205,610/Math.max(rows,1));
      ctx.fillStyle=accent;ctx.fillRect(780,y-55,3,110);
      metric(m,1010,y,200,68,'right');
    });
    if(selected.hero){
      const m=selected.hero, value=latinPosterDigits(m.value);
      ctx.save();ctx.direction='ltr';ctx.textAlign='left';ctx.font='italic 900 570px Barlow Condensed';
      const size=Math.min(570,570*800/ctx.measureText(value).width);
      ctx.font=`italic 900 ${size}px Barlow Condensed`;ctx.textBaseline='alphabetic';
      const shade=ctx.createLinearGradient(0,1060,0,1580);shade.addColorStop(0,'#ffffff');shade.addColorStop(.5,'#f4f8e9');shade.addColorStop(1,accent);
      ctx.fillStyle=shade;ctx.fillText(value,35,1530);ctx.restore();
      text(m.unit,970,1540,85,ink,160,900,'right');label(m.label,970,1600,32,muted,230,'right');
    }
  }
  else {
    all.forEach((m,i)=>{const y=400+i*185;const isHero=m.id===selected.hero?.id;metric(m,1016,y,380,isHero?95:64,'right');if(i<all.length-1){ctx.fillStyle='#ffffff38';ctx.fillRect(670,y+90,346,1);}});
  }
  // Below the route, clear of story username/reply overlays and workout metrics.
  const brand=light ? (o.lightLogoImage ?? o.logoImage) : o.logoImage;
  if(brand){
    const {x:logoX,y:logoY,width:logoWidth,height:logoHeight}=shareBrandBox(o.template,brand.width/brand.height);
    ctx.save();ctx.shadowColor='rgba(0,0,0,.5)';ctx.shadowBlur=6;ctx.shadowOffsetY=2;
    ctx.drawImage(brand,logoX,logoY,logoWidth,logoHeight);ctx.restore();
  }
  text('VO2PEAKS / TRAINING JOURNAL',64,H-60,16,muted,800,500);
  if(includeBackground && o.template!==7 && o.routeMode==='map' && o.mapImage){let line='',y=H-35;ctx.font='12px sans-serif';for(const word of ESRI_CREDIT.split(' ')){if(ctx.measureText(line+word).width>950){text(line,64,y,12,muted,950,400);line='';y+=15;}line+=word+' ';}text(line,64,y,12,muted,950,400);}
  if(output!==canvas){output.width=canvas.width;output.height=canvas.height;output.getContext('2d')?.drawImage(canvas,0,0);}
}
